<template>
  <q-page class="q-pa-md bg-grey-2">
    <!-- Header Banner Principal -->
    <q-card class="q-mb-md shadow-2 rounded-borders overflow-hidden">
      <div class="bg-primary text-white q-pa-md row items-center justify-between">
        <div class="row items-center">
          <q-icon name="system_update" size="md" class="q-mr-sm" />
          <div>
            <div class="text-h6 text-weight-bold">Gestión de Versiones de SISCORP</div>
            <div class="text-caption text-blue-2">Carga de instaladores, historial permanente, publicación y control de rollback</div>
          </div>
        </div>
        <q-btn
          color="white"
          text-color="primary"
          icon="cloud_upload"
          label="Subir Nueva Versión"
          unelevated
          rounded
          class="text-weight-bold"
          @click="abrirModalSubir"
        />
      </div>
    </q-card>

    <!-- Tabla Quasar de Histórico de Versiones -->
    <q-card class="shadow-2 rounded-borders">
      <q-table
        :data="listaVersiones"
        :columns="columns"
        row-key="id"
        :loading="loading"
        flat
        bordered
        class="versiones-table"
        no-data-label="No se han registrado versiones en el histórico."
      >
        <!-- Slot para Estado (Publicado vs Histórico) -->
        <template v-slot:body-cell-estado="props">
          <q-td :props="props" class="text-center">
            <q-chip
              v-if="props.row.estado === 1"
              color="positive"
              text-color="white"
              icon="check_circle"
              dense
              class="text-weight-bold q-px-sm"
            >
              DISPONIBLE PARA DESCARGAR (PUBLICADO)
            </q-chip>
            <q-chip
              v-else
              color="grey-7"
              text-color="white"
              icon="history"
              dense
              class="q-px-sm"
            >
              HISTÓRICO / INACTIVO
            </q-chip>
          </q-td>
        </template>

        <!-- Slot para Tamaño en MB -->
        <template v-slot:body-cell-tamano_bytes="props">
          <q-td :props="props" class="text-center">
            {{ formatBytes(props.row.tamano_bytes) }}
          </q-td>
        </template>

        <!-- Slot para Acciones -->
        <template v-slot:body-cell-acciones="props">
          <q-td :props="props" class="text-center q-gutter-xs">
            <!-- Botón Publicar / Revertir -->
            <q-btn
              v-if="props.row.estado !== 1"
              color="primary"
              dense
              unelevated
              icon="cloud_done"
              label="Activar / Publicar"
              class="q-px-xs text-weight-bold"
              @click="cambiarEstado(props.row, 1)"
            >
              <q-tooltip>Establecer esta versión como la activa disponible para descarga (Rollback / Publicar)</q-tooltip>
            </q-btn>
            <q-btn
              v-else
              color="warning"
              dense
              flat
              icon="pause_circle_outline"
              label="Desactivar"
              class="q-px-xs"
              @click="cambiarEstado(props.row, 0)"
            >
              <q-tooltip>Desactivar esta versión</q-tooltip>
            </q-btn>

            <!-- Botón Descargar ZIP directo -->
            <q-btn
              color="teal"
              dense
              flat
              round
              icon="get_app"
              @click="descargarArchivo(props.row.url_descarga)"
            >
              <q-tooltip>Descargar paquete ZIP manualmente</q-tooltip>
            </q-btn>

            <!-- Botón Eliminar de histórico -->
            <q-btn
              color="negative"
              dense
              flat
              round
              icon="delete"
              @click="eliminarVersion(props.row)"
            >
              <q-tooltip>Eliminar versión del histórico</q-tooltip>
            </q-btn>
          </q-td>
        </template>
      </q-table>
    </q-card>

    <!-- Modal Formulario para Subir Nueva Versión -->
    <q-dialog v-model="modalSubir" persistent>
      <q-card style="min-width: 450px; max-width: 600px;" class="rounded-borders">
        <q-card-section class="bg-primary text-white row items-center justify-between">
          <div class="text-h6 text-weight-bold">Subir Nueva Versión de SISCORP</div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="q-pa-md">
          <q-form @submit="subirVersion" class="q-gutter-md">
            <!-- Número de Versión -->
            <q-input
              v-model="form.version"
              outlined
              dense
              label="Número de Versión *"
              placeholder="Ejemplo: 1.0.5.0"
              hint="Formato semántico sugerido: Mayor.Menor.Compilación.Revisión"
              :rules="[val => !!val || 'El número de versión es requerido']"
            >
              <template v-slot:prepend>
                <q-icon name="style" />
              </template>
            </q-input>

            <!-- Selector de Archivo ZIP -->
            <q-file
              v-model="form.archivo"
              outlined
              dense
              label="Paquete de Actualización (.ZIP) *"
              accept=".zip"
              hint="Seleccione el archivo ZIP con los binarios de SISCORP"
              :rules="[val => !!val || 'Debe seleccionar un archivo ZIP']"
            >
              <template v-slot:prepend>
                <q-icon name="attach_file" />
              </template>
            </q-file>

            <!-- Notas de Cambio / Changelog -->
            <q-input
              v-model="form.notas_cambio"
              outlined
              dense
              type="textarea"
              rows="3"
              label="Notas de la Versión / Novedades"
              placeholder="Describa las mejoras, correcciones o cambios incluidos..."
            />

            <!-- Switch Publicar Inmediatamente -->
            <q-toggle
              v-model="form.publicar_inmediatamente"
              label="Establecer como versión activa inmediatamente al completar la carga"
              color="primary"
            />

            <!-- Barra de Progreso de Carga -->
            <div v-if="subiendo" class="q-mt-sm">
              <div class="text-caption text-weight-bold text-primary q-mb-xs">
                Subiendo archivo al servidor... {{ porcentajeSubida }}%
              </div>
              <q-linear-progress :value="porcentajeSubida / 100" color="primary" stripe animated />
            </div>

            <!-- Botones del Modal -->
            <div class="row justify-end q-gutter-sm q-mt-md">
              <q-btn label="Cancelar" color="grey" flat v-close-popup :disable="subiendo" />
              <q-btn
                type="submit"
                label="Subir Paquete"
                color="primary"
                unelevated
                class="text-weight-bold"
                :loading="subiendo"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import axios from "axios";

export default {
  name: "VersionesListaPage",
  data() {
    return {
      listaVersiones: [],
      loading: false,
      modalSubir: false,
      subiendo: false,
      porcentajeSubida: 0,
      form: {
        version: "",
        archivo: null,
        notas_cambio: "",
        publicar_inmediatamente: false,
      },
      columns: [
        { name: "id", label: "ID", field: "id", align: "center", sortable: true },
        { name: "version", label: "Versión", field: "version", align: "center", sortable: true },
        { name: "archivo", label: "Archivo ZIP", field: "archivo", align: "left" },
        { name: "fecha_publicacion", label: "Fecha de Carga", field: "fecha_publicacion", align: "center", sortable: true },
        { name: "tamano_bytes", label: "Tamaño", field: "tamano_bytes", align: "center" },
        { name: "estado", label: "Estado", field: "estado", align: "center", sortable: true },
        { name: "acciones", label: "Acciones", field: "acciones", align: "center" },
      ],
    };
  },
  mounted() {
    this.listar();
  },
  methods: {
    async listar() {
      this.loading = true;
      try {
        const response = await axios.post("ApiWeb/VersionesListar.php");
        if (!response.data.error) {
          this.listaVersiones = response.data.data || [];
        } else {
          this.notificar(response.data.mensaje || "Error al obtener versiones", "negative");
        }
      } catch (error) {
        this.notificar("Error de conexión al cargar la lista de versiones", "negative");
      } finally {
        this.loading = false;
      }
    },
    abrirModalSubir() {
      this.form = {
        version: "",
        archivo: null,
        notas_cambio: "",
        publicar_inmediatamente: false,
      };
      this.porcentajeSubida = 0;
      this.subiendo = false;
      this.modalSubir = true;
    },
    async subirVersion() {
      if (!this.form.version || !this.form.archivo) {
        this.notificar("Complete los campos obligatorios", "warning");
        return;
      }

      this.subiendo = true;
      this.porcentajeSubida = 0;

      const formData = new FormData();
      formData.append("version", this.form.version.trim());
      formData.append("archivo", this.form.archivo);
      formData.append("notas_cambio", this.form.notas_cambio);
      formData.append("publicar_inmediatamente", this.form.publicar_inmediatamente ? "1" : "0");

      try {
        const response = await axios.post("ApiWeb/VersionSubir.php", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total > 0) {
              this.porcentajeSubida = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          },
        });

        if (!response.data.error) {
          this.notificar(response.data.mensaje || "Versión subida con éxito", "positive");
          this.modalSubir = false;
          this.listar();
        } else {
          this.notificar(response.data.mensaje || "Error al subir la versión", "negative");
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.mensaje ? error.response.data.mensaje : "Error al subir el paquete de actualización";
        this.notificar(msg, "negative");
      } finally {
        this.subiendo = false;
      }
    },
    async cambiarEstado(row, nuevoEstado) {
      const titulo = nuevoEstado === 1 ? "Confirmar Publicación / Rollback" : "Confirmar Desactivación";
      const mensaje = nuevoEstado === 1
        ? `¿Desea establecer la versión ${row.version} como DISPONIBLE PARA DESCARGAR? Se desactivará la versión previa y los clientes de escritorio recibirán esta actualización.`
        : `¿Desea desactivar la versión ${row.version}?`;

      if (confirm(mensaje)) {
        try {
          const response = await axios.post("ApiWeb/VersionCambiarEstado.php", {
            id: row.id,
            estado: nuevoEstado,
          });
          if (!response.data.error) {
            this.notificar(response.data.mensaje || "Estado actualizado", "positive");
            this.listar();
          } else {
            this.notificar(response.data.mensaje || "Error al actualizar estado", "negative");
          }
        } catch (error) {
          this.notificar("Error de conexión al cambiar el estado de la versión", "negative");
        }
      }
    },
    async eliminarVersion(row) {
      if (row.estado === 1) {
        this.notificar("No puede eliminar la versión que actualmente está publicada y activa.", "warning");
        return;
      }

      if (confirm(`¿Esta seguro de eliminar permanentemente la versión ${row.version} y su archivo del servidor?`)) {
        try {
          const response = await axios.post("ApiWeb/VersionEliminar.php", {
            id: row.id,
          });
          if (!response.data.error) {
            this.notificar(response.data.mensaje || "Versión eliminada", "positive");
            this.listar();
          } else {
            this.notificar(response.data.mensaje || "Error al eliminar la versión", "negative");
          }
        } catch (error) {
          this.notificar("Error de conexión al eliminar la versión", "negative");
        }
      }
    },
    descargarArchivo(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },
    formatBytes(bytes) {
      if (!bytes || bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    notificar(mensaje, tipo = "positive") {
      if (this.$q && this.$q.notify) {
        this.$q.notify({
          message: mensaje,
          color: tipo,
          position: "top-right",
          timeout: 3000,
        });
      } else {
        alert(mensaje);
      }
    },
  },
};
</script>

<style scoped>
.versiones-table {
  background-color: #ffffff;
}
</style>
